// Difficulty tier 1 (easy). Templates are plain 30x20 ASCII grids; their
// placement geometry (entry/exit floor height, fill columns, eligibility) is
// derived automatically — see src/levels/templateGeometry.js. Sections chain
// continuously via a height offset, so there is no longer any startsHigh/endsHigh
// metadata: a "raised" variant of a puzzle is produced by rendering the base
// template at a height offset rather than by a duplicate template.
export const easyTemplates = {
  flat_ground: {
    layout: `
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: {},
  },
  spikes1: {
    layout: `
.....SS................SS.....
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: {},
  },
  pits1: {
    layout: `
BBBBB...BBBBB...BBBBB...BBBBBB
BBBBB...BBBBB...BBBBB...BBBBBB
`,
    metadata: {},
  },
  raised_platform1: {
    layout: `
..........BBBBBBBBBB..........
..............................
..............................
..............................
BBBBBB..................BBBBBB
BBBBBB..................BBBBBB
`,
    metadata: {},
  },
  raised_platform_up1: {
    layout: `
........................BBBBBB
........................BBBBBB
........................BBBBBB
..........BBBBBBBBBB....BBBBBB
........................BBBBBB
........................BBBBBB
BBBBBB..................BBBBBB
BBBBBB..................BBBBBB
`,
    metadata: {},
  },
  raised_platform_down1: {
    layout: `
BBBBBB........................
BBBBBB........................
BBBBBB........................
BBBBBB....BBBBBBBBBB..........
BBBBBB........................
BBBBBB........................
BBBBBB..................BBBBBB
BBBBBB..................BBBBBB
`,
    metadata: {},
  },
  projectiles1: {
    layout: `
..BB......................BB..
..BB......................pB..
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: {},
  },
  stairs_up1: {
    layout: `
...........................BBB
...........................BBB
......................BBBBBBBB
......................BBBBBBBB
.................BBBBBBBBBBBBB
.................BBBBBBBBBBBBB
............BBBBBBBBBBBBBBBBBB
............BBBBBBBBBBBBBBBBBB
.......BBBBBBBBBBBBBBBBBBBBBBB
.......BBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: {},
  },

  stair_down1: {
    layout: `
BBB...........................
BBB...........................
BBBBBBBB......................
BBBBBBBB......................
BBBBBBBBBBBBB.................
BBBBBBBBBBBBB.................
BBBBBBBBBBBBBBBBBB............
BBBBBBBBBBBBBBBBBB............
BBBBBBBBBBBBBBBBBBBBBBB.......
BBBBBBBBBBBBBBBBBBBBBBB.......
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: {},
  },

  // A recessed "sunk" crossing: walk the side plateaus, drop to the stepping
  // platform in the middle, climb back up. The dip below the entry floor means
  // this only becomes eligible once there's enough height for the drop. The
  // plateaus are thin — the fill-skirt extends them solid when raised.
  sunk_platform1: {
    layout: `
BBBBBBB.................BBBBBB
BBBBBBB.................BBBBBB
BBBBBBB....BBBBBBBBB....BBBBBB
BBBBBBB.................BBBBBB
`,
    metadata: {},
  },

  // Small ramps: gentle +2 / -2 height changes so terrain settles at
  // intermediate heights (not just the full-staircase extremes). These are the
  // connective pieces that make the continuous-offset chaining visible.
  step_up_pits1: {
    layout: `
...............BBBBBB...BBBBBB
...............BBBBBB...BBBBBB
BBBBBB...BBBBBBBBBBBB...BBBBBB
BBBBBB...BBBBBBBBBBBB...BBBBBB
`,
    metadata: {},
  },
  step_down_spikes1: {
    layout: `
......SS......................
BBBBBBBBBBBBBBB...............
BBBBBBBBBBBBBBB.......SS......
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: {},
  },
  wall_jump1: {
    layout: `
.BBBBBBBBBBBBBBBBBBB..........
.BBBBBBBBBBBBBBBBBBB.....BBBBB
.BBBBBBBBBBBBBBBBBBB.....BBBBB
.BBBBBBBBBBBBBBBBBBB.....BBBBB
.BBBBBBBBBBBBBBBBBBB.....BBBBB
.BBBBBBBBBBBBBBBBBBB.....BBBBB
.BBBBBBBBBBBBBBBBBBB.....BBBBB
.BBBBBBBBBBBBBBBBBBB.....BBBBB
.BBBBBBBBBBBBBBBBBBB.....BBBBB
.BBBBBBBBBBBBBBBBBBB.....BBBBB
.BBBBBBBBBBBBBBBBBBB.....BBBBB
.........................BBBBB
.........................BBBBB
.........................BBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: {},
  },

  // --- Wall-jump templates -------------------------------------------------
  // These use the wall-slide / wall-jump mechanic (see Player.js): the runner
  // can spring off a wall it's pressed against, launching the *other* way and up.
  // A vertical shaft formed by two walls lets the player zig-zag up it — slide
  // the front (right) wall, jump backward-up onto the back (left) wall, jump
  // forward-up off that, and so on — to reach a ledge a single jump can't.
  //
  // Authoring notes specific to these:
  //  - The two walls are spaced ~4 open cells apart: close enough that one
  //    wall-jump (≈5 cells of rise) carries you across to the opposite wall
  //    while still rising, so you re-grab and climb instead of falling out.
  //  - The BACK (left) wall is taller than the exit ledge so it still catches
  //    you near the top of the climb; the FRONT (right) wall doubles as the exit
  //    plateau you mount.
  //  - They set `metadata.contentTopRow` to the exit-ledge height, not the wall
  //    tops: the tall walls are the climbing surface, not an obstacle you need
  //    six cells of jump headroom over, so the default derivation would make
  //    them practically never eligible (see docs/level-generation.md).
  //
  // wall_climb1 (gentle intro): run into the pocket, bound up the right wall,
  // wall-jump back to the left wall, then forward onto the high plateau. No
  // hazards — this is where the player learns the move.
  wall_climb1: {
    layout: `
.............BBBB.............
.............BBBB.............
.............BBBB.............
.............BBBB.............
.............BBBB....BBBBBBBBB
.............BBBB....BBBBBBBBB
.............BBBB....BBBBBBBBB
.............BBBB....BBBBBBBBB
.............BBBB....BBBBBBBBB
.............BBBB....BBBBBBBBB
.....................BBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: { contentTopRow: 8 },
  },

  // wall_climb_tall1 (+8): the same intro shaft, one ledge higher.
  wall_climb_tall1: {
    layout: `
.............BBBB.............
.............BBBB.............
.............BBBB.............
.............BBBB.............
.............BBBB....BBBBBBBBB
.............BBBB....BBBBBBBBB
.............BBBB....BBBBBBBBB
.............BBBB....BBBBBBBBB
.............BBBB....BBBBBBBBB
.............BBBB....BBBBBBBBB
.............BBBB....BBBBBBBBB
.....................BBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: { contentTopRow: 9 },
  },

  // wall_descent1 (-6): the gentle counterpart — run off the high ledge into the
  // shaft and ride the wall down (the slow wall-slide grip controls the drop),
  // then slip out the doorway at the base to the low exit. No hazards; this is
  // where the player learns controlled descent. See the wall-jump notes above;
  // contentTopRow is the entry height (the wall runs above it as the slide
  // surface) so it stays eligible while H is high.
  wall_descent1: {
    layout: `
..............BBB.............
..............BBB.............
..............BBB.............
..............BBB.............
BBBBBBBBBB....BBB.............
BBBBBBBBBB....BBB.............
BBBBBBBBBB....BBB.............
BBBBBBBBBB....BBB.............
BBBBBBBBBB....BBB.............
BBBBBBBBBB....................
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
    metadata: { contentTopRow: 7 },
  },
};
